import { useEffect, useState } from 'react';
import api from '../../api/client';
import ConfirmDialog from '../common/ConfirmDialog';
import RotationBlockEditor from './RotationBlockEditor';

const DAY_PATTERN_LABELS = {
  daily: 'Todos los días',
  weekdays: 'Lunes a Viernes',
  saturday: 'Sábado',
  sunday: 'Domingo',
  specific: 'Día específico',
};

const PLAY_MODE_LABELS = {
  random: 'Aleatorio',
  sequential: 'Secuencial',
  weighted: 'Ponderado',
};

export default function RotationBlockList() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [blockToDelete, setBlockToDelete] = useState(null);
  const [editingBlock, setEditingBlock] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  useEffect(() => {
    fetchBlocks();
  }, []);

  const fetchBlocks = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/rotation-blocks');
      setBlocks(data || []);
    } catch (err) {
      console.error('[RotationBlockList] Failed to fetch blocks:', err);
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (block = null) => {
    setEditingBlock(block);
    setIsEditorOpen(true);
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditingBlock(null);
    fetchBlocks();
  };

  const handleEditorSaved = () => {
    fetchBlocks();
  };

  const handleDelete = (block) => {
    setBlockToDelete(block);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!blockToDelete) return;
    try {
      await api.delete(`/api/rotation-blocks/${blockToDelete.id}`);
      setBlocks(blocks.filter(b => b.id !== blockToDelete.id));
    } catch (err) {
      console.error('[RotationBlockList] Delete failed:', err);
    } finally {
      setConfirmOpen(false);
      setBlockToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Cargando bloques de rotación...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">Bloques de Rotación</h1>
          <p className="text-sm text-dark-400 mt-1">Programación aleatoria de shows en franjas horarias</p>
        </div>
        <button onClick={() => openEditor(null)} className="btn btn-primary btn-sm">
          + Nuevo Bloque
        </button>
      </div>

      {blocks.length === 0 ? (
        <div className="py-12 text-center text-dark-400">
          No hay bloques de rotación. Creá el primero para programar shows aleatorios.
        </div>
      ) : (
        <div className="space-y-4">
          {blocks.map((block) => (
            <div key={block.id} className="card hover:shadow-lg transition-shadow">
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h2 className="text-lg font-semibold text-dark-100">{block.name}</h2>
                    <p className="text-sm text-dark-500 mb-1">
                      {block.description || 'Sin descripción'}
                    </p>
                  </div>
                  <div className="text-right space-x-2">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        block.is_active ? 'bg-success-900 text-success-200' : 'bg-error-900 text-error-200'
                      }`}
                    >
                      {block.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary-900 text-primary-200">
                      {DAY_PATTERN_LABELS[block.day_pattern] || block.day_pattern}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div className="space-y-1">
                    <p className="text-xs text-dark-500">Horario:</p>
                    <p className="font-medium text-dark-100">
                      {block.start_time} – {block.end_time}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-dark-500">Modo:</p>
                    <p className="font-medium text-dark-100">
                      {PLAY_MODE_LABELS[block.play_mode] || block.play_mode}
                    </p>
                  </div>
                </div>
                {(block.shows || []).length > 0 && (
                  <div className="border-t border-dark-700 pt-3">
                    <p className="text-xs font-medium text-dark-400 mb-2">
                      Shows en rotación ({(block.shows || []).length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(block.shows || []).slice(0, 6).map((show) => (
                        <span
                          key={show.show_id || show.id}
                          className="px-2 py-1 rounded bg-dark-800/50 text-dark-300 text-[12px]"
                          title={show.show_name || show.name}
                        >
                          {show.show_name || show.name}
                        </span>
                      ))}
                      {(block.shows || []).length > 6 && (
                        <span className="px-2 py-1 rounded bg-dark-800/50 text-dark-400 text-[12px]">
                          +{(block.shows || []).length - 6} más
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end px-4 pb-3 space-x-2">
                <button
                  onClick={() => openEditor(block)}
                  className="btn btn-secondary btn-sm"
                >
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(block)}
                  className="btn btn-error btn-sm"
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Eliminar Bloque de Rotación"
        message="¿Seguro que quieres eliminar este bloque de rotación? Esta acción no se puede deshacer."
        danger
        onConfirm={confirmDelete}
        onClose={() => {
          setConfirmOpen(false);
          setBlockToDelete(null);
        }}
      />
      {isEditorOpen && (
        <RotationBlockEditor
          key={editingBlock?.id || 'new'}
          block={editingBlock}
          onSaved={handleEditorSaved}
          onClose={closeEditor}
        />
      )}
    </div>
  );
}